<template>
  <div>
    <v-card-actions class="ai-container">
      <p class="ai-text">{{ helpText }}</p>
      <v-btn icon @click="generateSuggestion" class="ai-wand-btn" :disabled="!!aiSuggestion || loading">
        <v-icon v-if="loading" color="primary">fas fa-spinner fa-spin</v-icon>
        <v-icon v-else color="primary" size="22">fa-wand-magic-sparkles</v-icon>
      </v-btn>
    </v-card-actions>

    <div class="ai-box-wrapper">
      <v-card v-if="aiSuggestion" class="ai-box pa-3 mt-4" outlined>
        <v-btn icon small @click="clearSuggestion" class="ai-close-btn">
          <v-icon>$close</v-icon>
        </v-btn>

        <div class="d-flex justify-space-between align-center">
          <p class="ai-suggestion-text"><strong>AI Suggestion:</strong> {{ aiSuggestion }}</p>
        </div>

        <div class="ai-actions mt-2 d-flex align-center">
          <v-icon small icon class="thumbs-up" color="primary">fa-thumbs-up</v-icon>
          <span class="ml-1">Like this? Use the suggestion.</span>
          <o-btn small outlined class="ml-3 font-weight-bold" @click="tryAgain" :disabled="loading">Try Again</o-btn>
          <o-btn small color="primary" class="ml-3 use-btn" @click="useSuggestion">Use This</o-btn>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { OnvivaiService } from "@/service";
import OBtn from "@/framework/OBtn.vue";
import { AiInteractionRecordItem } from "@/service";

import type { PropType } from "vue";
import type { logRefTable } from "@/service/index.js"
export default Vue.extend({
  name: "AiSuggestion",
  components: { OBtn },
  props: {
    logRefTable: {
      type: String as PropType<logRefTable>,
      required: true,
    },
    logRefId: {
      type: Number,
      required: true,
      default: 0,
    },
    text: {
      type: String,
      required: false,
      default: "",
    },
    target: {
      type: String,
      required: true,
      validator: (value: string) =>
        ["subject", "question", "question_and_type", "generate_questions", "generate_answers"].includes(value),
    },
    helpText: {
      type: String,
      default: "Need help crafting an engaging subject? Click the magic wand for AI suggestions!"
    }
  },
  data: () => ({
    aiSuggestion: "",
    loading: false,
    userId: undefined as number | undefined,
    larId: 0 as number,
  }),
  methods: {
    async generateSuggestion(): Promise<void> {
      this.loading = true;
      try {
        const requestBody = {
          logRefTable: this.logRefTable,
          logRefId: Number(this.logRefId),
          target: this.target as "subject" | "question" | "question_and_type" | "generate_questions, generate_answers",
          text: this.text || "Default text for AI suggestion",
        };

        const response = await OnvivaiService.getAiSuggestion({ requestBody });

        if (response && response.aiResponse) {
          this.aiSuggestion = response.aiResponse;
          this.userId = response.userId;
          this.larId = response.larId;
        } else {
          this.aiSuggestion = "No suggestion available.";
        }
      } catch (error) {
        console.error("Error fetching AI suggestion:", error);
        this.aiSuggestion = "Error generating suggestion. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    async logInteraction(action: "retried" | "used"): Promise<void> {
      try {
        await OnvivaiService.createAiInteraction({
          requestBody: {
            userId: this.userId,
            logAIEventId: this.larId,
            aiAction: AiInteractionRecordItem.aiAction[action.toUpperCase() as keyof typeof AiInteractionRecordItem.aiAction],
            payload: JSON.stringify({
              suggestion: this.aiSuggestion,
            }),
            _ver: 1,
          },
        });
      } catch (err) {
        console.error("AI Interaction logging failed:", err);
      }
    },
    async tryAgain(): Promise<void> {
      await this.logInteraction("retried");
      this.generateSuggestion();
    },
    async useSuggestion(): Promise<void> {
      await this.logInteraction("used");
      this.$emit("useSuggestion", this.aiSuggestion);
      this.aiSuggestion = "";
    },
    clearSuggestion(): void {
      this.aiSuggestion = "";
    }
  }
});
</script>

<style lang="scss" scoped>
.ai-box-wrapper {
  display: flex;
  justify-content: flex-end; /* Push the AI box to the right */
  width: 100%;
}
.ai-container {
  display: flex;
  align-items: center;
  padding: 0;
  gap: 2px;
  overflow: hidden !important; /* Prevents unwanted blurring */
  box-shadow: none !important;
}
.ai-text {
  margin: 0;
  padding: 0;
  font-size: 16px;
  line-height: 1.5;
  display: inline;
  color: black !important;
}
.ai-wand-btn {
  min-width: unset !important;
  padding: 0 !important;
  margin: 0 !important;
  margin-bottom: 10px !important;
}
.ai-box {
  position: relative;
  background: #f8f8f8;
  border: 1px solid #ccc;
  border-radius: 5px;
  padding: 15px;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.ai-close-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
}
.ai-suggestion-text {
  padding-right: 40px; /* Prevents text overlapping the close button */
  overflow-wrap: break-word; /* Alternative for better support */
  max-width: 100%;
}
</style>